import { DuplicateRecordDialog } from "@/components/DuplicateRecordDialog/DuplicateRecordDialog";
import { Callout, toast } from "@/components/ui";
import { AVAILABLE_ACTIONS_QUERY_KEYS, QueryKeys } from "@/constants/Query";
import { videoCallRoomTokenAtom } from "@/features/AudioVideo/atoms/videoCallAtoms";
import { Chart } from "@/features/Charting/Chart";
import { Chat } from "@/features/Chat/Chat";
import { MedicalRecordsContainer } from "@/features/Patient/MedicalRecords/MedicalRecordsContainer";
import { PatientBar } from "@/features/Patient/PatientBar/PatientBar";
import { useDuplicateRecordAlert } from "@/hooks/useDuplicateRecordAlert";
import { queryClient } from "@/lib/queryClient";
import { cn, wildcardInvalidateQuery } from "@/lib/utils";
import { useEncounter } from "@/network/useEncounter";
import { useUser } from "@/network/useUser";
import { deserializeData } from "@/socket/hooks/useChartSocketEvents";
import { socketsAtom } from "@/store/socketsAtom";
import { getId } from "@curai/js-fhir-tools";
import { ErrorBoundary } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  retainSearchParams,
  useBlocker,
  useNavigate,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useAtomValue } from "jotai";
import { useCallback, useEffect } from "react";
import { z } from "zod";

let lastInvalidatedPatientId: string | null = null;

const searchSchema = z.object({
  encounterId: z.union([z.number(), z.string()]).optional(),
  chatTab: z.enum(["live", "async", "combined"]).optional(),
});

export const Route = createFileRoute("/patient/$patientId")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [retainSearchParams(["encounterId", "chatTab"])],
  },
  loader: async ({ params }) => {
    // Only perform invalidations when navigating to a different patient
    if (lastInvalidatedPatientId === params.patientId) {
      return;
    }
    lastInvalidatedPatientId = params.patientId;
    // Invalidate all available actions queries that are related to the patient's chart
    // This will inherently add some latency to the page load, but it's worth it
    // to avoid stale actions.
    // Note: Chat messages are kept up-to-date via socket events and aggressive
    // refetching (refetchOnMount + staleTime: 0) instead of loader invalidation
    // to avoid blocking navigation.
    for (const queryKey of AVAILABLE_ACTIONS_QUERY_KEYS) {
      wildcardInvalidateQuery(
        queryClient,
        queryKey.filter((key) => key !== undefined),
      );
    }
  },
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const { data: me } = useUser();
  const videoCallRoomToken = useAtomValue(videoCallRoomTokenAtom);
  const navigate = useNavigate({ from: Route.fullPath });
  const { encounterId } = Route.useSearch();
  const { patientId } = Route.useParams();

  const {
    isDuplicateDialogOpen,
    duplicatePatients,
    dismissDuplicateDialog,
    suppressDuplicateDialogForPatient,
  } = useDuplicateRecordAlert(patientId);

  useBlocker({
    shouldBlockFn: ({ current, next }) => {
      const { patientId: currentPatientId } = current.params as {
        patientId: string;
      };
      const { patientId: nextPatientId } = next.params as {
        patientId: string;
      };
      if (videoCallRoomToken && currentPatientId !== nextPatientId) {
        toast.error(
          "Please end the ongoing video call before navigating to another patient chat. If you are currently not in a video call, then hard refresh the page and try again.",
        );
        return true;
      }
      return false;
    },
    enableBeforeUnload: Boolean(videoCallRoomToken), // Only enable beforeunload if there is an active video call
  });

  const chartingSocket = useAtomValue(socketsAtom)?.charting;

  const { data: encounterData, isLoading: isEncounterLoading } = useEncounter(
    encounterId?.toString(),
    patientId,
  );

  useEffect(() => {
    // If the encounter does not belong to the patient, navigate to the patient's chart
    // and clear the encounterId from the URL
    if (encounterData && patientId) {
      const encounterPatientId = getId(encounterData.subject?.reference);
      if (encounterPatientId && encounterPatientId !== patientId) {
        navigate({
          search: (prev) => ({ ...prev, encounterId: undefined }),
          replace: true,
        });
      }
    }
  }, [encounterData, patientId, navigate]);

  const handlePatientChartUpdated = useCallback(
    (data: {
      requester: string;
      resources: Record<string, object[]>;
    }) => {
      const { requester, resources } = deserializeData(data);

      if (requester && me?.userId === Number(requester)) {
        // Do nothing since we are the one who updated the chart
      } else {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.PatientId(
            patientId as string,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Demographics.DEFAULT(patientId as string),
        });

        // Invalidate eRx status updates triggered by eRx webhooks
        if (resources.medicationRequest) {
          const encounterIds = new Set<string>();

          for (const medicationRequest of resources.medicationRequest) {
            const encounterId = getId(medicationRequest.encounter);
            if (encounterId) {
              encounterIds.add(encounterId);
            }
          }

          for (const encounterId of encounterIds) {
            queryClient.invalidateQueries({
              queryKey: QueryKeys.Patient.Chart.Prescriptions.DEFAULT(
                patientId as string,
                encounterId,
              ),
            });
          }
        }

        if (resources.requestGroup?.length) {
          // Emits should always be for the same patient
          const patientId = getId(resources.requestGroup[0].subject) as string;
          const encounterIds = new Set<string>();

          for (const requestGroup of resources.requestGroup) {
            const encounterId = getId(requestGroup.encounter);
            if (encounterId) {
              encounterIds.add(encounterId);
            }
          }

          for (const encounterId of encounterIds) {
            queryClient.invalidateQueries({
              queryKey: QueryKeys.Patient.Chart.Labs.DEFAULT({
                patientId,
                encounterId,
              }),
            });
          }

          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.MedicalRecords.Labs.DEFAULT(patientId),
          });
        }
      }

      // This one always has to happen for height/weight observations -> eRx availability
      if (resources.observation?.length) {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Prescriptions.DEFAULT_PATIENT(
            patientId as string,
          ),
        });
      }
    },
    [patientId, queryClient, me],
  );

  useEffect(() => {
    if (!chartingSocket) return;

    // These are intentionally not cleaned up here to make sure emits are still received
    // for patient charts that aren't in focus. They should only be cleaned up when
    // the patient's chart is removed from the provider's list of open charts.
    // This can happen two ways:
    // 1. The provider manually removes the chart by clicking the X button in the patient tab.
    //    This is handled by the useRemovePatientTab hook.
    // 2. The patient is removed from the provider's list of patients via state machine changes.
    //    This is handled server-side when the patient is removed from the provider's list of patients.

    if (encounterId) {
      chartingSocket.emit("join_charting_for_encounter", {
        encounterId,
      });
    }

    if (patientId) {
      chartingSocket.emit("join_charting_for_patient", {
        patientId,
      });
    }

    // Handle patient chart updated events
    chartingSocket.on("patient_chart_updated", handlePatientChartUpdated);

    return () => {
      chartingSocket.off("patient_chart_updated", handlePatientChartUpdated);
    };
  }, [chartingSocket, encounterId, patientId, handlePatientChartUpdated]);

  return (
    <div className="w-full h-full flex flex-col min-h-0">
      <DuplicateRecordDialog
        isOpen={isDuplicateDialogOpen}
        onOpenChange={(open) => {
          if (!open) dismissDuplicateDialog();
        }}
        duplicatePatients={duplicatePatients}
        onOpenDuplicateChart={suppressDuplicateDialogForPatient}
      />
      <PatientBar patientId={patientId} encounterId={encounterId?.toString()} />

      <div className={cn("w-full h-full gap-2 px-4 pb-4 min-h-0 flex")}>
        <MedicalRecordsContainer key={patientId} />
        {!encounterId || (encounterId && !isEncounterLoading) ? (
          <ErrorBoundary
            fallback={
              <div className="h-full w-full flex items-center justify-center">
                <Callout
                  variant="danger"
                  title="An error has occurred loading the requested chat"
                  description="Please refresh the page. If the problem persists, please use the feedback tool to report the issue."
                />
              </div>
            }
          >
            <Chat key={`${patientId}-${encounterId}`} />
          </ErrorBoundary>
        ) : null}
        {encounterId && !isEncounterLoading ? (
          <ErrorBoundary
            fallback={
              <div className="h-full w-full flex items-center justify-center">
                <Callout
                  variant="danger"
                  title="An error has occurred loading the requested chart"
                  description="Please refresh the page. If the problem persists, please use the feedback tool to report the issue."
                />
              </div>
            }
          >
            <Chart key={encounterId} />
          </ErrorBoundary>
        ) : null}
      </div>
    </div>
  );
}
